import type { MetricInfo } from '../components/ui/MetricInfoModal';

/**
 * Bilingual (English + Bangla) help for every KPI shown on the Billing home
 * (`/billing`) and the Top Sheet (`/billing/top-sheet`). Each entry explains
 * what the figure means and which operational ledger it is derived from, so the
 * two pages stay in sync from a single source of truth.
 *
 * Naming rule: the UI says “Total Bill” (not “Net Revenue”), “Patient Dues”
 * (not “Receivables”) and “Total Payables”. Those friendly labels are used
 * consistently on the Dashboard, Billing home and Top Sheet; the older
 * accounting words only appear here as aliases where they help.
 */
export const BILLING_METRIC_INFO = {
  totalIncome: {
    en: {
      what: 'Total billed for the period after discounts: patient bills (OPD + IPD), diagnostic receipts and pharmacy sales. This is the same figure some reports call “net revenue”.',
      source:
        'Derived from three ledgers: `invoices` (OPD & IPD billing), `diagnostic_receipts` and `pharmacy_sales`. Income = gross − discount. IPD advances are excluded because a deposit is a liability, not income.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ে ছাড় বাদ দেওয়ার পর মোট বিল: রোগীর বিল (OPD + IPD), ডায়াগনস্টিক রিসিট এবং ফার্মেসি বিক্রয়। কিছু রিপোর্টে এটিকে “নিট রেভিনিউ” বলা হয়।',
      source:
        'তিনটি লেজার থেকে আসে: `invoices` (OPD ও IPD বিলিং), `diagnostic_receipts` এবং `pharmacy_sales`। আয় = গ্রস − ছাড়। IPD অগ্রিম বাদ দেওয়া হয়, কারণ জমা একটি দায় (liability), আয় নয়।',
    },
  },
  collected: {
    en: {
      what: 'Actual money received during the period across every payment mode, split by care setting (OPD, IPD, pharmacy). A due collected today counts today, not on the day the bill was raised. IPD advances are included in IPD Collected. Cash collected is not the same as income earned.',
      source:
        'Cash-basis: `payments.payment_date` for invoices, `diagnostic_receipt_payments.paid_at` plus each receipt’s up-front amount on its creation date, `pharmacy_sales.sold_at` and `admissions.advance_paid` (admitted_at). This is the same date the ledger uses to mirror cash into the daily cashbook.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ে সব পেমেন্ট পদ্ধতিতে প্রকৃতপক্ষে গৃহীত টাকা, রোগী OPD, IPD নাকি ফার্মেসি—সেই অনুযায়ী ভাগ করা। আজ আদায় করা বকেয়া আজই গণনা হয়, বিল তৈরির দিনে নয়। IPD অগ্রিম IPD Collected-এ অন্তর্ভুক্ত। নগদ আদায় আর অর্জিত আয় এক নয়।',
      source:
        'ক্যাশ-ভিত্তিক: ইনভয়েসের জন্য `payments.payment_date`, ডায়াগনস্টিকের প্রাথমিক আদায় রিসিপ্ট তৈরির তারিখে এবং পরবর্তী বকেয়া `diagnostic_receipt_payments.paid_at`, ফার্মেসির `pharmacy_sales.sold_at` এবং IPD অগ্রিম `admissions.admitted_at`। লেজারও একই তারিখে ক্যাশবুকে ক্যাশ মিরর করে।',
    },
  },
  totalExpenses: {
    en: {
      what: 'Every cost the hospital incurred in the period: referral partner payments, payroll, operating expenses (rent, utilities, supplies) and fixed-asset purchases.',
      source:
        'Derived from `revenue_distributions` (referral shares), `payrolls`, `expenses` and `fixed_assets` for the selected date range.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ে হাসপাতালের সব খরচ: রেফারেল পার্টনার পেমেন্ট, বেতন, পরিচালন ব্যয় (ভাড়া, ইউটিলিটি, সাপ্লাই) এবং স্থায়ী সম্পদ ক্রয়।',
      source:
        'নির্বাচিত তারিখের মধ্যে `revenue_distributions` (রেফারেল শেয়ার), `payrolls`, `expenses` এবং `fixed_assets` থেকে আসে।',
    },
  },
  referralShare: {
    en: {
      what: 'Commission paid to registered referral partners for sending patients. A partner can be a doctor, an employee or an outside agent — all three are paid from this one ledger. The breakdown splits the commission into the doctor share, employee share and other-partner share. It is a hospital expense for the period. Doctors earn only through this referral share. The unpaid part is a payable to the partner, not an extra expense.',
      source:
        'Derived from `revenue_distributions` rows with a `referral_id` (diagnostic referral shares) for the selected period, joined to `referral_partners` to classify each share as DOCTOR, EMPLOYEE or OTHER. Settled amounts reduce the referral payable; outstanding amounts appear under Total Payables.',
    },
    bn: {
      what: 'রোগী রেফার করার জন্য নিবন্ধিত রেফারেল পার্টনারদের দেওয়া কমিশন। পার্টনার ডাক্তার, কর্মী বা বাইরের এজেন্ট হতে পারেন — তিন ধরনের পেমেন্টই এই একই লেজার থেকে হয়। বিস্তারিত অংশে কমিশন ডাক্তার শেয়ার, কর্মী শেয়ার এবং অন্যান্য পার্টনার শেয়ারে ভাগ করা হয়। এটি নির্দিষ্ট সময়ের হাসপাতালের খরচ। ডাক্তাররা শুধু এই রেফারেল শেয়ার থেকেই আয় করেন। অপরিশোধিত অংশ পার্টনারের প্রতি দায়, অতিরিক্ত খরচ নয়।',
      source:
        'নির্বাচিত সময়ের `revenue_distributions`-এর যেসব সারিতে `referral_id` আছে (ডায়াগনস্টিক রেফারেল শেয়ার) সেখান থেকে আসে, এবং `referral_partners`-এর সাথে যুক্ত হয়ে প্রতিটি শেয়ার DOCTOR, EMPLOYEE বা OTHER হিসেবে শ্রেণীবদ্ধ হয়। পরিশোধিত টাকা রেফারেল পাওনা কমায়; বাকি টাকা Total Payables-এ দেখায়।',
    },
  },
  dueCollected: {
    en: {
      what: 'Money received in the period that settled a bill raised earlier. “Current-period due” is the due part of bills raised in the selected period; “Old due” is money collected today against bills raised before the period. It is a subset of Cash Collected — counter tenders and IPD advances are not dues.',
      source:
        'Cash-basis: `payments.payment_date` (invoice dues), later `diagnostic_receipt_payments.paid_at` (diagnostic dues) and later `pharmacy_sale_payments.paid_at` (pharmacy dues). A bill is “old” when its creation date is before the selected period start. IPD advances and the amount paid when a diagnostic receipt or pharmacy sale is created are excluded.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ে গৃহীত টাকা যা আগে তৈরি করা বিলের বকেয়া পরিশোধ করেছে। “বর্তমান সময়ের বকেয়া” হলো নির্বাচিত সময়ে তৈরি বিলের বকেয়া অংশ; “পুরনো বকেয়া” হলো নির্বাচিত সময়ের আগে তৈরি বিলের বিপরীতে আজ আদায় করা টাকা। এটি Cash Collected-এর একটি অংশ — কাউন্টারে সরাসরি প্রদত্ত টাকা এবং IPD অগ্রিম বকেয়া নয়।',
      source:
        'ক্যাশ-ভিত্তিক: ইনভয়েসের `payments.payment_date`, পরবর্তী `diagnostic_receipt_payments.paid_at` এবং পরবর্তী `pharmacy_sale_payments.paid_at`। বিল তৈরির তারিখ নির্বাচিত সময়ের শুরুর আগে হলে সেটি “পুরনো”। IPD অগ্রিম এবং ডায়াগনস্টিক রিসিট/ফার্মেসি বিক্রয় তৈরির সময় প্রদত্ত টাকা বাদ দেওয়া হয়।',
    },
  },
  patientDues: {
    en: {
      what: 'Money patients still owe the hospital for services already delivered. This is a running all-time balance, not limited to the selected period. It is also known as “receivables”.',
      source:
        'Sum of unpaid balances (`due_amount > 0`) on `invoices`, `diagnostic_receipts` and `pharmacy_sales`.',
    },
    bn: {
      what: 'ইতিমধ্যে দেওয়া সেবার জন্য রোগীদের কাছে এখনও বকেয়া টাকা। এটি সর্বকালীন চলমান ব্যালেন্স, নির্বাচিত সময়ের মধ্যে সীমাবদ্ধ নয়। “রিসিভেবল” নামেও পরিচিত।',
      source:
        '`invoices`, `diagnostic_receipts` এবং `pharmacy_sales`-এ অপরিশোধিত ব্যালেন্সের (`due_amount > 0`) সমষ্টি।',
    },
  },
  totalPayables: {
    en: {
      what: 'Money the hospital still owes to referral partners and suppliers. The referral portion is the unpaid part of shares already counted under Expenses, so it is a liability — never a second income or expense.',
      source:
        'Sum of unsettled referral-partner shares, unpaid pharmacy supplier invoices and unpaid asset vendor invoices. This is an all-time position.',
    },
    bn: {
      what: 'রেফারেল পার্টনার ও সরবরাহকারীদের কাছে হাসপাতালের এখনও বকেয়া টাকা। রেফারেল অংশ হলো Expenses-এ ধরা শেয়ারের অপরিশোধিত অংশ, তাই এটি দায় — দ্বিতীয় আয় বা খরচ নয়।',
      source:
        'অপরিশোধিত রেফারেল পার্টনার শেয়ার, অপরিশোধিত ফার্মেসি সরবরাহকারী ইনভয়েস এবং অপরিশোধিত অ্যাসেট বিক্রেতা ইনভয়েসের সমষ্টি। এটি সর্বকালীন অবস্থা।',
    },
  },
  payroll: {
    en: {
      what: 'Net salary expense for the period after deductions.',
      source:
        'Derived from the `payrolls` table, filtered by `period_start`/`period_end` falling inside the selected date range.',
    },
    bn: {
      what: 'কর্তনের পর নির্দিষ্ট সময়ের নিট বেতন ব্যয়।',
      source:
        '`payrolls` টেবিল থেকে আসে, যেখানে `period_start`/`period_end` নির্বাচিত তারিখের মধ্যে পড়ে।',
    },
  },
  fixedAssets: {
    en: {
      what: 'Capital spent on fixed assets (equipment, machinery, furniture) during the period.',
      source: 'Derived from the `fixed_assets` table using each asset’s `purchase_date`.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ে স্থায়ী সম্পদে (যন্ত্রপাতি, মেশিনারি, আসবাবপত্র) ব্যয়িত মূলধন।',
      source: '`fixed_assets` টেবিল থেকে প্রতিটি সম্পদের `purchase_date` অনুযায়ী আসে।',
    },
  },
  operatingExpenses: {
    en: {
      what: 'Day-to-day running expenses for the period, such as utilities, rent and supplies.',
      source: 'Derived from the `expenses` table using each entry’s `expense_date`.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ের দৈনন্দিন পরিচালন ব্যয়, যেমন ইউটিলিটি, ভাড়া ও সাপ্লাই।',
      source: '`expenses` টেবিল থেকে প্রতিটি এন্ট্রির `expense_date` অনুযায়ী আসে।',
    },
  },
  cashbookBalance: {
    en: {
      what: 'Running drawer balance as at the end of the period: the balance carried from earlier days plus cash in minus cash out. The same figure appears on the Cashbook, Top Sheet and dashboard.',
      source:
        'Derived from `cashbook` movements (debit = in, credit = out). `opening` and `closing` rows are snapshots and are excluded from totals; a difference between the physical count and the book is stored as an `adjustment` movement.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ের শেষে চলমান ড্রয়ার ব্যালেন্স: আগের দিনগুলো থেকে আনা ব্যালেন্স যোগ নগদ-আয়, বিয়োগ নগদ-ব্যয়। একই সংখ্যা Cashbook, Top Sheet ও ড্যাশবোর্ডে দেখায়।',
      source:
        '`cashbook` মুভমেন্ট থেকে আসে (debit = আয়, credit = ব্যয়)। `opening` ও `closing` এন্ট্রি স্ন্যাপশট, তাই মোটে যোগ হয় না; শারীরিক গণনা ও বইয়ের পার্থক্য `adjustment` মুভমেন্ট হিসেবে রাখা হয়।',
    },
  },
  netProfit: {
    en: {
      what: 'Profit remaining after subtracting every cost from total income for the period.',
      source:
        'Total income minus total expenses (referral pay + payroll + fixed assets + operating expenses). A negative value means a loss.',
    },
    bn: {
      what: 'নির্দিষ্ট সময়ের মোট আয় থেকে সব খরচ বাদ দেওয়ার পর অবশিষ্ট লাভ।',
      source:
        'মোট আয় বিয়োগ মোট ব্যয় (রেফারেল পে + বেতন + স্থায়ী সম্পদ + পরিচালন ব্যয়)। ঋণাত্মক মান মানে ক্ষতি।',
    },
  },
  riskFund: {
    en: {
      what: 'Amounts moved to the Risk Fund. These are patient dues deemed uncollectible and removed from active receivables.',
      source: 'Derived from the risk_fund_entries table.',
    },
    bn: {
      what: 'রিস্ক ফান্ডে স্থানান্তরিত পরিমাণ। এগুলি অনাদায়ী রোগীর বকেয়া যা সক্রিয় রিসিভেবল থেকে সরিয়ে নেওয়া হয়েছে।',
      source: 'risk_fund_entries টেবিল থেকে আসে।',
    },
  },
} satisfies Record<string, MetricInfo>;

/** Keys of {@link BILLING_METRIC_INFO}. */
export type BillingMetricKey = keyof typeof BILLING_METRIC_INFO;
